// Signed-out is a detour, not a destination.
//
// §05 gives this app exactly one way in — `/api/auth/session/login`, which 302s to
// Keycloak — so there is nothing for a signed-out visitor to decide. Showing
// them a card with a single button is a click that only ever has one answer,
// and after a sign-out it leaves the previous user's screen on display until
// they think to reload. Every signed-out visit goes to Keycloak instead: a
// fresh load, a session that expired mid-visit, a deliberate sign-out followed
// by navigating back. There is no "you have already been sent once" exception —
// that idea is what `signInLoopBreaker` replaced, and why.
//
// Two things still hold it back, and only two:
//
//   1. Only `unauthenticated` redirects. A session endpoint that answered 502
//      is not a login problem, and `/api/auth/session/login` is served by that same
//      dead API — so an unreachable API shows the reason instead of bouncing a
//      corpse. That is the `AnonymousCause` discriminator.
//   2. The circuit breaker, which counts attempts per episode rather than
//      remembering that one ever happened. See `signInLoopBreaker.ts`.
//
// There is no environment this is turned off in. Every deployment, local
// included, authenticates the same way — an unauthenticated `/api/*` request is
// a 401 and `/api/auth/session/login` is always mounted — so this hook is the only path
// a signed-out visitor takes anywhere.

import { useEffect, useRef, useState } from "react";
import { assignLocation } from "./navigation.js";
import type { AuthState } from "./principal.js";
import { currentPath, loginUrl } from "./session.js";
import {
  clearAttempts,
  decideSignInRedirect,
  readAttempts,
  writeAttempts,
} from "./signInLoopBreaker.js";

/**
 * `undecided` is the moment before the breaker has been consulted — rendered as
 * "going to sign in" rather than as the card, so the card never flashes on its
 * way to a redirect that was always going to happen.
 */
type Outcome = "undecided" | "redirecting" | "blocked";

/**
 * Sends an unauthenticated visitor to Keycloak. Returns whether sign-in is
 * under way, so the caller can say so instead of offering a door.
 */
export function useAutoSignIn(auth: AuthState): boolean {
  const shouldSignIn = auth.status === "anonymous" && auth.cause === "unauthenticated";
  const [outcome, setOutcome] = useState<Outcome>("undecided");
  // The breaker must be consulted EXACTLY once per episode. React's StrictMode
  // runs an effect, tears it down and runs it again on mount, and both passes
  // observe the same render's state — so a state flag would be read as stale
  // and the attempt counted twice, halving the budget in development only. A
  // ref survives that, because it belongs to the component instance.
  const decided = useRef(false);

  useEffect(() => {
    if (auth.status === "authenticated") {
      // A session exists, so whatever the browser was doing before, it was not
      // looping. Close the episode: the next sign-out starts from a clean slate.
      clearAttempts();
      decided.current = false;
      setOutcome("undecided");
      return;
    }
    if (!shouldSignIn || decided.current) return;
    decided.current = true;

    const decision = decideSignInRedirect(readAttempts(), Date.now());
    if (!decision.allow) {
      setOutcome("blocked");
      return;
    }

    writeAttempts(decision.next);
    setOutcome("redirecting");
    // Read the path at navigation time: the provider sits above the router, so
    // a value captured when the session resolved would name whichever page the
    // visitor first landed on, not the one they are on now.
    assignLocation(loginUrl(currentPath()));
  }, [auth.status, shouldSignIn]);

  return shouldSignIn && outcome !== "blocked";
}
